import { useField } from "formik";
import React, { useEffect, useId, useRef, useState } from "react";
import { loadGoogleMapsScript } from "utils/googleMapsLoader";
import styles from "./admin.module.scss";
import cx from "./cx";
import { FieldShell } from "./fields";

/** What a picked Google Places address fills in. */
export interface PickedAddress {
  streetAddress: string;
  postalCode: string;
  city: string;
  lat: number;
  lon: number;
  formattedAddress: string;
}

interface Suggestion {
  placeId: string;
  mainText: string;
  secondaryText: string;
  prediction: any;
}

export interface AddressFieldProps {
  label?: React.ReactNode;
  help?: React.ReactNode;
  error?: React.ReactNode;
  required?: boolean;
  id?: string;
  name?: string;
  placeholder?: string;
  fieldClassName?: string;
  value: string;
  /** Free text typed by the user. */
  onChange: (value: string) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /** A suggestion was picked: street, postcode, city and coordinates. */
  onPick: (address: PickedAddress) => void;
}

function component(components: any[], type: string): string {
  const c = components.find((x) => x?.types?.includes(type));
  return c?.longText ?? c?.long_name ?? "";
}

function readLatLng(location: any): { lat: number; lon: number } | null {
  if (!location) return null;
  const lat =
    typeof location.lat === "function" ? location.lat() : location.lat;
  const lon =
    typeof location.lng === "function" ? location.lng() : location.lng;
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  return { lat, lon };
}

/** Rounds to 6 decimals (~10 cm), enough for a mosque. */
const round6 = (n: number) => Math.round(n * 1e6) / 1e6;

/**
 * Address input with Google Places (New) suggestions. Typing works as a
 * normal text field; picking a suggestion fills street, postcode, city and
 * lat/lon through `onPick`.
 */
export const AddressField: React.FC<AddressFieldProps> = ({
  label,
  help,
  error,
  required,
  id,
  name,
  placeholder = "Begynn å skrive adressen …",
  fieldClassName,
  value,
  onChange,
  onBlur,
  onPick,
}) => {
  const autoId = useId();
  const fid = id ?? autoId;
  const readyRef = useRef(false);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [picking, setPicking] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sessionRef = useRef<any>(null);
  const latestQuery = useRef("");

  useEffect(() => {
    let alive = true;
    loadGoogleMapsScript()
      .then(() => window.google.maps.importLibrary?.("places"))
      .then(() => {
        if (!alive || !window.google?.maps?.places?.AutocompleteSuggestion)
          return;
        sessionRef.current =
          new window.google.maps.places.AutocompleteSessionToken();
        readyRef.current = true;
        // Text typed while the script was loading still gets suggestions.
        if (latestQuery.current) search(latestQuery.current);
      })
      .catch((err) => console.error("Google Places kunne ikke lastes:", err));
    return () => {
      alive = false;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const search = async (input: string) => {
    latestQuery.current = input;
    if (!readyRef.current || input.trim().length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    try {
      const { suggestions: result } =
        await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
          {
            input,
            sessionToken: sessionRef.current,
            language: "no",
            region: "no",
          },
        );
      if (latestQuery.current !== input) return;
      const mapped: Suggestion[] = (result || [])
        .filter((s: any) => s.placePrediction)
        .map((s: any) => ({
          placeId: s.placePrediction.placeId,
          mainText: s.placePrediction.mainText?.text || "",
          secondaryText: s.placePrediction.secondaryText?.text || "",
          prediction: s.placePrediction,
        }));
      setSuggestions(mapped);
      setActive(-1);
      setOpen(mapped.length > 0);
    } catch (err) {
      console.error("Adresseforslag feilet:", err);
      setSuggestions([]);
      setOpen(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    onChange(next);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(next), 250);
  };

  const pick = async (s: Suggestion) => {
    setOpen(false);
    setPicking(true);
    onChange(s.mainText);
    try {
      const place = s.prediction.toPlace
        ? s.prediction.toPlace()
        : new window.google.maps.places.Place({ id: s.placeId });
      await place.fetchFields({
        fields: ["addressComponents", "location", "formattedAddress"],
      });
      const parts: any[] = place.addressComponents || [];
      const route = component(parts, "route");
      const number = component(parts, "street_number");
      const street = [route, number].filter(Boolean).join(" ") || s.mainText;
      const coords = readLatLng(place.location);
      if (!coords) throw new Error("Stedet mangler koordinater");
      onChange(street);
      onPick({
        streetAddress: street,
        postalCode: component(parts, "postal_code"),
        city:
          component(parts, "postal_town") ||
          component(parts, "locality") ||
          component(parts, "administrative_area_level_2"),
        lat: round6(coords.lat),
        lon: round6(coords.lon),
        formattedAddress: place.formattedAddress || s.mainText,
      });
    } catch (err) {
      console.error("Kunne ikke hente adressen fra Google:", err);
    } finally {
      // A session ends with the details call; start a new one.
      sessionRef.current =
        new window.google.maps.places.AutocompleteSessionToken();
      setSuggestions([]);
      setPicking(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === "Enter" && active >= 0) {
      e.preventDefault();
      pick(suggestions[active]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const listId = `${fid}-list`;

  return (
    <FieldShell
      id={fid}
      label={label}
      required={required}
      help={help}
      error={error}
      className={fieldClassName}
    >
      <div className={styles.addr}>
        <input
          id={fid}
          name={name}
          type="text"
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={
            open && active >= 0 ? `${listId}-${active}` : undefined
          }
          aria-invalid={error ? true : undefined}
          aria-describedby={
            error ? `${fid}-err` : help ? `${fid}-help` : undefined
          }
          className={cx(styles.inp, !!error && styles.inpErr)}
          value={value}
          placeholder={placeholder}
          disabled={picking}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          onBlur={(e) => {
            setTimeout(() => setOpen(false), 150);
            onBlur?.(e);
          }}
        />
        {open && (
          <ul className={styles.addrList} id={listId} role="listbox">
            {suggestions.map((s, i) => (
              <li
                key={s.placeId}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={i === active}
                className={cx(i === active && styles.addrActive)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(s)}
              >
                <b>{s.mainText}</b>
                {s.secondaryText && <span>{s.secondaryText}</span>}
              </li>
            ))}
            <li className={styles.addrBrand} aria-hidden="true">
              Forslag fra Google
            </li>
          </ul>
        )}
      </div>
    </FieldShell>
  );
};

type FormikAddressFieldProps = Omit<
  AddressFieldProps,
  "value" | "onChange" | "onBlur" | "onPick"
> & {
  name: string;
  onPick: (address: PickedAddress) => void;
};

/** AddressField bound to a Formik text field. */
export const FormikAddressField: React.FC<FormikAddressFieldProps> = ({
  name,
  onPick,
  ...rest
}) => {
  const [field, meta, helpers] = useField<string>(name);
  return (
    <AddressField
      {...rest}
      name={name}
      value={field.value ?? ""}
      onChange={(v) => helpers.setValue(v)}
      onBlur={field.onBlur}
      onPick={onPick}
      error={meta.touched && meta.error ? meta.error : undefined}
    />
  );
};

export default AddressField;
